import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  GuidanceTourStep,
  GuidanceTourStepCompletionMap,
  OnboardingStepAccessibilityMap,
} from '../types';
import { GuidanceTourStepSearch } from './GuidanceTourStepSearch';
import { StepDependencyHint } from './StepDependencyHint';
import {
  buildChecklistHierarchy,
  filterChecklistHierarchy,
  getStepSubGroup,
  isFlatChecklistGroup,
} from '../utils/guidanceTourChecklistHierarchy';
import type { ChecklistStepEntry } from '../utils/guidanceTourChecklistHierarchy';
import {
  getOnboardingStepIconColor,
  getOnboardingStepTextColor,
  OnboardingStepDisplayState,
  resolveOnboardingStepDisplayState,
} from '../utils/onboardingStepStatusColors';

interface GuidanceTourChecklistProps {
  steps: GuidanceTourStep[];
  stepIndex: number;
  stepCompleted: GuidanceTourStepCompletionMap;
  stepAccessibility: OnboardingStepAccessibilityMap;
  open: boolean;
  fillHeight?: boolean;
  useContentHeight?: boolean;
  scrollContainerRef?: React.MutableRefObject<HTMLDivElement | null>;
  onJumpToStep: (index: number) => void;
  onJumpToPrerequisiteStep: (stepId: string) => void;
}

const StatusIcon: React.FC<{
  completed: boolean;
  isCurrent: boolean;
  displayState: OnboardingStepDisplayState;
}> = ({ completed, isCurrent, displayState }) => {
  const theme = useTheme();
  const iconSx = {
    fontSize: 14,
    color: getOnboardingStepIconColor(theme, displayState),
  };

  if (isCurrent) {
    return <PlayCircleOutlineIcon sx={iconSx} />;
  }
  if (completed) {
    return <CheckCircleIcon sx={iconSx} />;
  }
  return <RadioButtonUncheckedIcon sx={iconSx} />;
};

const makeSubGroupKey = (groupName: string, subGroupName: string) =>
  `${groupName}::${subGroupName}`;

function orderStepIdsByTour(
  stepIds: string[],
  steps: GuidanceTourStep[],
): string[] {
  const order = new Map(steps.map((step, index) => [step.id, index]));
  return [...stepIds].sort(
    (left, right) => (order.get(left) ?? 0) - (order.get(right) ?? 0),
  );
}

function getAccumulatedBlockedBy(
  items: ChecklistStepEntry[],
  stepAccessibility: OnboardingStepAccessibilityMap,
  steps: GuidanceTourStep[],
): string[] {
  const blocked = new Set<string>();

  items.forEach(({ step }) => {
    const accessibility = stepAccessibility[step.id];
    if (accessibility && !accessibility.accessible) {
      accessibility.blocked_by.forEach((stepId) => blocked.add(stepId));
    }
  });

  return orderStepIdsByTour(Array.from(blocked), steps);
}

function subGroupHasBlockedStep(
  items: ChecklistStepEntry[],
  stepAccessibility: OnboardingStepAccessibilityMap,
): boolean {
  return items.some(({ step }) => {
    const accessibility = stepAccessibility[step.id];
    return accessibility ? !accessibility.accessible : false;
  });
}

function allItemsCompleted(
  items: ChecklistStepEntry[],
  stepCompleted: GuidanceTourStepCompletionMap,
): boolean {
  return (
    items.length > 0 &&
    items.every(({ step }) => stepCompleted[step.id] === true)
  );
}

const SubGroupCompleteIcon: React.FC<{
  completed: boolean;
  align?: 'left' | 'right';
}> = ({ completed, align = 'left' }) => {
  const theme = useTheme();

  if (!completed) {
    return null;
  }

  return (
    <CheckCircleIcon
      sx={{
        fontSize: 14,
        color: getOnboardingStepIconColor(theme, 'completed'),
        flexShrink: 0,
        ...(align === 'right' ? { ml: 0.5 } : { mr: 0.375 }),
      }}
    />
  );
};

const StepList: React.FC<{
  items: ChecklistStepEntry[];
  steps: GuidanceTourStep[];
  stepIndex: number;
  stepCompleted: GuidanceTourStepCompletionMap;
  stepAccessibility: OnboardingStepAccessibilityMap;
  currentStepRef: React.Ref<HTMLDivElement>;
  onJumpToStep: (index: number) => void;
  onJumpToPrerequisiteStep: (stepId: string) => void;
  onClearSearch: () => void;
  listPaddingLeft?: number;
}> = ({
  items,
  steps,
  stepIndex,
  stepCompleted,
  stepAccessibility,
  currentStepRef,
  onJumpToStep,
  onJumpToPrerequisiteStep,
  onClearSearch,
  listPaddingLeft = 1.5,
}) => {
  const theme = useTheme();

  return (
  <List
    dense
    disablePadding
    sx={{
      pl: listPaddingLeft,
      '& .MuiListItemButton-root': {
        py: 0.125,
        minHeight: 22,
      },
    }}
  >
    {items.map(({ step, index }) => {
      const completed = stepCompleted[step.id] === true;
      const isCurrent = index === stepIndex;
      const accessibility = stepAccessibility[step.id];
      const isAccessible = accessibility ? accessibility.accessible : true;
      const blockedBy = accessibility?.blocked_by ?? [];
      const displayState = resolveOnboardingStepDisplayState({
        completed,
        isAccessible,
      });
      const stepTextColor = getOnboardingStepTextColor(theme, displayState);

      const listItem = (
        <ListItemButton
          dense
          selected={isCurrent}
          aria-disabled={!isAccessible}
          tabIndex={isAccessible ? 0 : -1}
          ref={isCurrent ? currentStepRef : undefined}
          onClick={() => {
            if (!isAccessible) {
              return;
            }
            onClearSearch();
            onJumpToStep(index);
          }}
          sx={{
            py: 0.125,
            px: 0.5,
            minHeight: 22,
            borderRadius: 0.75,
            cursor: isAccessible ? 'pointer' : 'default',
            '&.Mui-selected': {
              bgcolor: 'action.selected',
            },
            '&.Mui-selected:hover': {
              bgcolor: 'action.selected',
            },
            '&[aria-disabled="true"]': {
              cursor: 'default',
            },
            '&[aria-disabled="true"]:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 22, mr: 0.375, py: 0 }}>
            <StatusIcon
              completed={completed}
              isCurrent={isCurrent}
              displayState={displayState}
            />
          </ListItemIcon>
          <ListItemText
            disableTypography
            sx={{ my: 0, py: 0 }}
            primary={
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  lineHeight: 1.25,
                  fontWeight: isCurrent ? 600 : 400,
                  color: stepTextColor,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {step.title}
              </Typography>
            }
          />
        </ListItemButton>
      );

      if (!isAccessible && blockedBy.length > 0) {
        return (
          <StepDependencyHint
            key={step.id}
            blockedBy={blockedBy}
            steps={steps}
            stepIndex={stepIndex}
            stepCompleted={stepCompleted}
            onJumpToPrerequisiteStep={onJumpToPrerequisiteStep}
          >
            {listItem}
          </StepDependencyHint>
        );
      }

      return <React.Fragment key={step.id}>{listItem}</React.Fragment>;
    })}
  </List>
  );
};

export const GuidanceTourChecklist: React.FC<GuidanceTourChecklistProps> = ({
  steps,
  stepIndex,
  stepCompleted,
  stepAccessibility,
  open,
  fillHeight = false,
  useContentHeight = false,
  scrollContainerRef,
  onJumpToStep,
  onJumpToPrerequisiteStep,
}) => {
  const theme = useTheme();
  const [stepSearch, setStepSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedSubGroups, setExpandedSubGroups] = useState<Record<string, boolean>>({});
  const internalScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const currentStepRef = useRef<HTMLDivElement>(null);

  const assignScrollContainerRef = (node: HTMLDivElement | null) => {
    internalScrollContainerRef.current = node;
    if (scrollContainerRef) {
      scrollContainerRef.current = node;
    }
  };

  const hierarchy = useMemo(() => buildChecklistHierarchy(steps), [steps]);

  const filteredHierarchy = useMemo(
    () => filterChecklistHierarchy(hierarchy, stepSearch),
    [hierarchy, stepSearch],
  );

  const currentStep = steps[stepIndex];
  const currentGroupName = currentStep?.group ?? 'General';
  const currentSubGroupName = currentStep ? getStepSubGroup(currentStep) : '';
  const currentSubGroupKey = makeSubGroupKey(currentGroupName, currentSubGroupName);

  useEffect(() => {
    if (!open) {
      setStepSearch('');
    }
  }, [open]);

  useEffect(() => {
    if (!open || !currentStep) {
      return;
    }

    setExpandedGroups((prev) => ({
      ...prev,
      [currentGroupName]: true,
    }));
    if (!isFlatChecklistGroup(currentGroupName)) {
      setExpandedSubGroups((prev) => ({
        ...prev,
        [currentSubGroupKey]: true,
      }));
    }
  }, [open, currentGroupName, currentSubGroupKey, currentStep, stepIndex]);

  useEffect(() => {
    if (!open || !currentStepRef.current) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      currentStepRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [open, stepIndex, expandedGroups, expandedSubGroups, filteredHierarchy.length]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const toggleSubGroup = (subGroupKey: string) => {
    setExpandedSubGroups((prev) => ({
      ...prev,
      [subGroupKey]: !prev[subGroupKey],
    }));
  };

  const buildExpandedState = (expanded: boolean) => {
    const nextGroups: Record<string, boolean> = {};
    const nextSubGroups: Record<string, boolean> = {};

    hierarchy.forEach(group => {
      nextGroups[group.groupName] = expanded;
      if (!isFlatChecklistGroup(group.groupName)) {
        group.subGroups.forEach(subGroup => {
          nextSubGroups[makeSubGroupKey(group.groupName, subGroup.subGroupName)] =
            expanded;
        });
      }
    });

    return { nextGroups, nextSubGroups };
  };

  const expandAllGroups = () => {
    const { nextGroups, nextSubGroups } = buildExpandedState(true);
    setExpandedGroups(nextGroups);
    setExpandedSubGroups(nextSubGroups);
  };

  const collapseAllGroups = () => {
    const { nextGroups, nextSubGroups } = buildExpandedState(false);
    setExpandedGroups(nextGroups);
    setExpandedSubGroups(nextSubGroups);
  };

  const isSearching = stepSearch.trim().length > 0;
  const showExpandCollapseAll = !isSearching && hierarchy.length > 0;
  const expandCollapseActionSx = {
    fontSize: '0.625rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: 'text.secondary',
    px: 0.25,
    py: 0.125,
    borderRadius: 0.5,
    lineHeight: 1.2,
    textTransform: 'none' as const,
    '&:hover': {
      color: 'text.primary',
      bgcolor: 'action.hover',
    },
  };

  const checklistContent = (
    <Box
      sx={{
        position: 'relative',
        borderTop: fillHeight ? 0 : 1,
        borderColor: 'divider',
        mt: fillHeight ? 0 : 1.25,
        pt: fillHeight ? 0 : 0.75,
        mx: fillHeight ? 0 : -0.25,
        ...(fillHeight && !useContentHeight
          ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }
          : {}),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          minHeight: fillHeight ? 24 : 28,
          mb: 0,
          pr: 3.5,
        }}
      >
        {showExpandCollapseAll ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <ButtonBase onClick={expandAllGroups} sx={expandCollapseActionSx}>
              Expand all
            </ButtonBase>
            <Typography
              component="span"
              variant="caption"
              sx={{ fontSize: '0.625rem', color: 'text.disabled', px: 0.125 }}
            >
              |
            </Typography>
            <ButtonBase onClick={collapseAllGroups} sx={expandCollapseActionSx}>
              Collapse all
            </ButtonBase>
          </Box>
        ) : (
          <Box />
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: fillHeight ? 0 : 4,
          right: 4,
          zIndex: 2,
        }}
      >
        <GuidanceTourStepSearch value={stepSearch} onChange={setStepSearch} />
      </Box>

      <Box
        ref={assignScrollContainerRef}
        sx={{
          ...(useContentHeight
            ? {}
            : fillHeight
              ? { flex: 1, minHeight: 0, overflowY: 'auto' }
              : { maxHeight: 240, overflowY: 'auto' }),
          ...(useContentHeight ? { overflowY: 'visible' } : {}),
          pr: 3.5,
          '&::-webkit-scrollbar': {
            width: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: 4,
            bgcolor: 'action.disabled',
          },
        }}
      >
          {filteredHierarchy.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', px: 0.5, pb: 0.75 }}
            >
              No steps match your search.
            </Typography>
          ) : (
            filteredHierarchy.map((group, groupIndex) => {
              const groupExpanded = isSearching || expandedGroups[group.groupName] === true;
              const isFlatGroup = isFlatChecklistGroup(group.groupName);
              const allGroupItems = group.subGroups.flatMap((subGroup) => subGroup.items);
              const flatGroupItems = isFlatGroup ? allGroupItems : [];
              const groupAllComplete = allItemsCompleted(allGroupItems, stepCompleted);
              const groupDisplayState: OnboardingStepDisplayState = groupAllComplete
                ? 'completed'
                : 'pending';
              const groupTextColor = getOnboardingStepTextColor(theme, groupDisplayState);

              return (
                <Box
                  key={group.groupName}
                  sx={{
                    mb: groupIndex < filteredHierarchy.length - 1 ? 0.5 : 0,
                  }}
                >
                  <Box
                    onClick={() => toggleGroup(group.groupName)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 0.25,
                      py: 0.25,
                      cursor: 'pointer',
                      borderRadius: 0.75,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={`${groupExpanded ? 'Collapse' : 'Expand'} ${group.groupName}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        toggleGroup(group.groupName);
                      }}
                      sx={{
                        p: 0.25,
                        mr: 0.25,
                        transform: groupExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    >
                      <ExpandMoreIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        letterSpacing: '0.07em',
                        textTransform: 'uppercase',
                        lineHeight: 1.2,
                        color: groupTextColor,
                      }}
                    >
                      {group.groupName}
                    </Typography>
                    <SubGroupCompleteIcon completed={groupAllComplete} align="right" />
                  </Box>

                  <Collapse in={groupExpanded}>
                    <Box sx={{ pl: isFlatGroup ? 1 : 1.25 }}>
                      {isFlatGroup ? (
                        <StepList
                          items={flatGroupItems}
                          steps={steps}
                          stepIndex={stepIndex}
                          stepCompleted={stepCompleted}
                          stepAccessibility={stepAccessibility}
                          currentStepRef={currentStepRef}
                          onJumpToStep={onJumpToStep}
                          onJumpToPrerequisiteStep={onJumpToPrerequisiteStep}
                          onClearSearch={() => setStepSearch('')}
                          listPaddingLeft={0.5}
                        />
                      ) : (
                        group.subGroups.map((subGroup) => {
                        const subGroupKey = makeSubGroupKey(
                          group.groupName,
                          subGroup.subGroupName,
                        );
                        const subGroupExpanded =
                          isSearching || expandedSubGroups[subGroupKey] === true;
                        const hasCurrentStep = subGroup.items.some(
                          ({ index }) => index === stepIndex,
                        );
                        const subGroupBlocked = subGroupHasBlockedStep(
                          subGroup.items,
                          stepAccessibility,
                        );
                        const subGroupBlockedBy = getAccumulatedBlockedBy(
                          subGroup.items,
                          stepAccessibility,
                          steps,
                        );
                        const subGroupAllComplete = allItemsCompleted(
                          subGroup.items,
                          stepCompleted,
                        );
                        const subGroupDisplayState: OnboardingStepDisplayState =
                          subGroupAllComplete
                            ? 'completed'
                            : subGroupBlocked
                              ? 'disabled'
                              : 'pending';
                        const subGroupTextColor = getOnboardingStepTextColor(
                          theme,
                          subGroupDisplayState,
                        );

                        const subGroupHeader = (
                          <Box
                            onClick={() => toggleSubGroup(subGroupKey)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              px: 0.25,
                              py: 0.125,
                              cursor: 'pointer',
                              borderRadius: 0.75,
                              bgcolor: hasCurrentStep ? 'action.hover' : 'transparent',
                              '&:hover': {
                                bgcolor: 'action.hover',
                              },
                            }}
                          >
                            <IconButton
                              size="small"
                              aria-label={`${subGroupExpanded ? 'Collapse' : 'Expand'} ${subGroup.subGroupName}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleSubGroup(subGroupKey);
                              }}
                              sx={{
                                p: 0.25,
                                mr: 0.25,
                                transform: subGroupExpanded
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            >
                              <ExpandMoreIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.6875rem',
                                fontWeight: 600,
                                color: subGroupTextColor,
                                lineHeight: 1.2,
                              }}
                            >
                              {subGroup.subGroupName}
                            </Typography>
                            <SubGroupCompleteIcon
                              completed={subGroupAllComplete}
                              align="right"
                            />
                          </Box>
                        );

                        return (
                          <Box key={subGroupKey} sx={{ mb: 0.25 }}>
                            {subGroupBlocked && subGroupBlockedBy.length > 0 ? (
                              <StepDependencyHint
                                blockedBy={subGroupBlockedBy}
                                steps={steps}
                                stepIndex={stepIndex}
                                stepCompleted={stepCompleted}
                                onJumpToPrerequisiteStep={onJumpToPrerequisiteStep}
                                openOnClick={false}
                              >
                                {subGroupHeader}
                              </StepDependencyHint>
                            ) : (
                              subGroupHeader
                            )}

                            <Collapse in={subGroupExpanded}>
                              <StepList
                                items={subGroup.items}
                                steps={steps}
                                stepIndex={stepIndex}
                                stepCompleted={stepCompleted}
                                stepAccessibility={stepAccessibility}
                                currentStepRef={currentStepRef}
                                onJumpToStep={onJumpToStep}
                                onJumpToPrerequisiteStep={onJumpToPrerequisiteStep}
                                onClearSearch={() => setStepSearch('')}
                              />
                            </Collapse>
                          </Box>
                        );
                      })
                      )}
                    </Box>
                  </Collapse>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
  );

  if (fillHeight || useContentHeight) {
    return checklistContent;
  }

  return <Collapse in={open}>{checklistContent}</Collapse>;
};

export default GuidanceTourChecklist;
